import gsap, { Power2 } from 'gsap';
import { PerspectiveCamera, Scene, Vector2, Raycaster, WebGLRenderer, sRGBEncoding, Clock, PCFSoftShadowMap, Group } from 'three';
// AxesHelper / OrbitControls stay out of the production bundle (controls: false, debug axes unused on graph.cat).
// import { AxesHelper } from 'three';
// import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

import { Basics, isDebug, isSmartphone } from '../core/Basics';
import { GetBy } from '../core/Element';
import { Sizes } from '../core/Sizes';
import { CENTER, SETTINGS } from '../../../3D/constants';
import { Maths } from '../utils/Maths';
import { Metrics } from '../core/Metrics';
import { Interaction } from '../core/Interaction';

export default class WebGLSketch {
	_started = false;
	_paused = false;
	static renderer;
	static domElement;
	static scene;
	static camera;
	static clock;
	static size;
	static controls;

	static group = new Group();
	static mouse = new Vector2();
	static cameraGroup = new Group();
	static cameraPosition = CENTER;
	static cameraLerp = {
		z: .03
	};

	static raycaster = new Raycaster();
	static isRaycast = false;
	static isInteractive = false;
	static interactives = [];
	static dragging = false;

	static calls = {
		click: (e) => this.click(e)
	}

	static defaults = {
		container: 'scene-three',
		antialias: true,
		alpha: false,
		ortho: false,
		camera2D: SETTINGS.camera2D,
		shadows: false,
		debug: isDebug,
		transparent: true,
		clearColorAlpha: 0,
		lookAt: SETTINGS.lookAt,
		cameraPos: SETTINGS.cameraPos,
		fov: SETTINGS.fov,
		controls: SETTINGS.controls,
		far: SETTINGS.far,
		near: SETTINGS.near,
		clearColor: SETTINGS.clearColor,
		clearColorAlpha: SETTINGS.clearColorAlpha,
	}

	static init(opts = {}) {
		this.defaults = {
			...this.defaults,
			...opts
		};

		this.domElement = GetBy.id(this.defaults.container);
		this.domElement.style.opacity = 0;
		const { width, height } = this.domElement.getBoundingClientRect();
		this.size = this.defaults.size ? this.defaults.size : new Vector2(width, height);

		this.renderer = new WebGLRenderer({
			canvas: this.domElement,
			antialias: this.defaults.antialias,
			alpha: this.defaults.alpha
		});

		this.renderer.setClearColor(this.defaults.clearColor, this.defaults.clearColorAlpha);
		this.renderer.setSize(this.size.x, this.size.y);
		this.renderer.setPixelRatio(Sizes.RATIO_CANVAS);

		if (this.defaults.outputEncoding) {
			this.renderer.outputEncoding = this.defaults.outputEncoding;
		}

		if (this.defaults.shadows) {
			this.renderer.shadowMap.enabled = true;
			this.renderer.shadowMap.type = PCFSoftShadowMap;
		}

		this.camera = new PerspectiveCamera(
			this.defaults.fov,
			this.size.x / this.size.y,
			this.defaults.near,
			this.defaults.far
		);

		this.camera.position.copy(CENTER);
		this.cameraPosition.copy(this.defaults.cameraPos);
		this.cameraGroup.position.copy(this.defaults.cameraPos);

		if (this.defaults.camera2D) {
			this.camera.fov = 2 * Math.atan(this.size.x / this.camera.aspect / (2 * this.defaults.cameraPos.z)) * (180 / Math.PI);
		}

		this.camera.updateProjectionMatrix();

		this.scene = new Scene();
		this.scene.add(this.cameraGroup);
		this.cameraGroup.add(this.camera);

		// lookAt must run after the camera is parented, otherwise worldDir stays +Z
		// and every mesh at z<=0 is behind the camera (OrbitControls used to fix this).
		this.camera.lookAt(this.defaults.lookAt);

		// if (this.defaults.debug) {
		// 	const axesHelper = new AxesHelper(2000);
		// 	this.scene.add(axesHelper);
		// }

		// this.controls = new OrbitControls(this.camera, this.domElement);
		// this.controls.enableDamping = true;
		// this.controls.enabled = this.defaults.controls;
		this.controls = null;

		this.clock = new Clock();

		this.scene.add(this.group);
	}

	static time() {
		return this.clock.getElapsedTime();
	}

	static add(__obj) {
		this.group.add(__obj);
	}

	static remove(__obj) {
		this.group.remove(__obj);
	}

	static addInteractive(obj) {
		if (!this.interactives.includes(obj)) {
			this.interactives.push(obj);
		}
	}

	static removeInteractive(obj) {
		this.interactives = this.interactives.filter(o => o !== obj);
	}

	static start() {
		if (this._started && !this._paused) return;

		if (!this._started) this.addEventListeners();

		this._paused = false;
		this._started = true;
		this.clock.start();

		if (this.defaults.controls) this.domElement.style.pointerEvents = 'initial';
		else this.domElement.style.pointerEvents = 'none';
	}

	static stop() {
		if (!this._started) return;
		if (this._paused) return;
		this._paused = true;
		this.clock.stop();
	}

	static show() {
		gsap.to(this.domElement, { opacity: 1, duration: .5, ease: Power2.easeOut });
	}

	static addEventListeners() {
		GetBy.id('Canvas').addEventListener(Basics.clickEvent, this.calls.click);
	}

	static removeEventListeners() {
		GetBy.id('Canvas').removeEventListener(Basics.clickEvent, this.calls.click);
	}

	static raycast() {
		if (!this.isInteractive) return;

		const { x, y } = Interaction.positions.mouse;
		this.mouse.x = (x / Metrics.WIDTH) * 2 - 1;
		this.mouse.y = -(y / Metrics.HEIGHT) * 2 + 1;

		this.raycaster.setFromCamera(this.mouse, this.camera);

		const intersects = this.raycaster.intersectObjects(this.interactives, true);
		if (intersects.length > 0) {
			const first = intersects[0].object;
			if (first.userData?.isClickable) {
				first.parent.hover = true;
				this.isRaycast = true;
				this.hoverIn();
			}
		}
	}

	static reset() {
		if (!this.isRaycast) return;
		this.isRaycast = false;
		this.hoverOut();

		for (let i = 0; i < this.interactives.length; i++) {
			this.interactives[i].parent.hover = false;
		}
	}

	static click(e) {
		if (!this._started || this._paused || this.dragging) return;

		// On touch devices, read coordinates from the event directly
		// because Interaction.positions.mouse only updates on touchmove,
		// so a tap without movement leaves it stale
		if (e) {
			let clientX, clientY;
			if (e.changedTouches && e.changedTouches.length > 0) {
				clientX = e.changedTouches[0].clientX;
				clientY = e.changedTouches[0].clientY;
			} else if (e.touches && e.touches.length > 0) {
				clientX = e.touches[0].clientX;
				clientY = e.touches[0].clientY;
			} else {
				clientX = e.clientX;
				clientY = e.clientY;
			}
			this.mouse.x = (clientX / Metrics.WIDTH) * 2 - 1;
			this.mouse.y = -(clientY / Metrics.HEIGHT) * 2 + 1;
		}

		this.raycaster.setFromCamera(this.mouse, this.camera);
		const intersects = this.raycaster.intersectObjects(this.interactives, true);
		if (intersects.length > 0) {
			const first = intersects[0].object;
			if (first.userData?.isClickable) {
				first.userData.onClick(first);
			}
		}
	}

	static hoverIn() {
		this.domElement.parentNode.style.cursor = 'pointer';
	}

	static hoverOut() {
		this.domElement.parentNode.style = '';
	}

	static moveCamera({ x, y, z = SETTINGS.cameraPos.z }) {
		this.cameraPosition.x = x;
		this.cameraPosition.y = y;
		this.cameraPosition.z = z;
	}

	static update() {
		let lerpZ = this.cameraLerp.z;
		let targetZ = this.cameraPosition.z;

		if (!this.dragging) {
			lerpZ = .88;
			const dx = this.cameraPosition.x - this.cameraGroup.position.x;
			const dy = this.cameraPosition.y - this.cameraGroup.position.y;
			const distance = Math.sqrt(dx * dx + dy * dy);
			const increment = 250; //(Math.random() > .5 ? -1 : 1) * 250;
			const minZ = SETTINGS.cameraPos.z;
			const maxZ = SETTINGS.cameraPos.z + increment;
			targetZ = Maths.map(distance, 0, 800, minZ, maxZ);
		}

		this.cameraGroup.position.x = this.cameraPosition.x;
		this.cameraGroup.position.y = this.cameraPosition.y;
		this.cameraGroup.position.z = Maths.lerp(this.cameraGroup.position.z, targetZ, lerpZ);
	}

	static render() {
		this.renderer.render(this.scene, this.camera);
	}

	static loop() {
		if (!this._started || this._paused) return;

		this.reset();
		this.raycast();

		this.update();
		this.render();
	}

	static resize() {
		if (isSmartphone) {
			// On smartphone, CSS controls the canvas size via --vh !important.
			// We must sync the renderer buffer to the actual displayed size,
			// without touching inline styles (updateStyle=false).
			const { width, height } = this.domElement.getBoundingClientRect();
			if (width === this.size.x && height === this.size.y) return;

			this.size.set(width, height);
			this.renderer.setSize(this.size.x, this.size.y, false);

			this.camera.aspect = this.size.x / this.size.y;
			if (this.defaults.camera2D) {
				this.camera.fov = 2 * Math.atan(this.size.x / this.camera.aspect / (2 * this.defaults.cameraPos.z)) * (180 / Math.PI);
			}
			this.camera.updateProjectionMatrix();
			return;
		}

		this.domElement.style = '';
		const { width, height } = this.domElement.getBoundingClientRect();

		if (this._started) this.domElement.style.opacity = 1;
		else this.domElement.style.opacity = 0;

		if (width === this.size.x && height === this.size.y) return;

		this.size.set(width, height);
		this.renderer.setSize(this.size.x, this.size.y);

		this.camera.aspect = this.size.x / this.size.y;
		this.cameraGroup.position.z = this.defaults.cameraPos.z;

		if (this.defaults.camera2D) {
			this.camera.fov = 2 * Math.atan(this.size.x / this.camera.aspect / (2 * this.defaults.cameraPos.z)) * (180 / Math.PI);
		}

		this.camera.updateProjectionMatrix();
	}

	static dispose() {
		this.removeEventListeners();
	}
}
